import { z } from 'zod';
import { isValidTimezone } from '../time/index.js';

/**
 * Building blocks for request/form schemas. Every input object schema is
 * `z.strictObject` so unknown keys (e.g. a smuggled `tenantId`) are rejected.
 */

export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const PINCODE_RE = /^[1-9][0-9]{5}$/;
export const FSSAI_RE = /^[0-9]{14}$/;
export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const CIDR_RE = /^(?:(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?|[0-9a-fA-F:]+(?:\/\d{1,3})?)$/;

/** '' and null from forms mean "no value". */
const blankToNull = (v) => (v === '' || v === undefined ? null : v);

/** Wrap a schema so '' / null / undefined are accepted and normalised to null. */
export function optional(schema) {
  return z.preprocess(blankToNull, schema.nullable()).optional();
}

export const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');
export const email = z.string().trim().toLowerCase().max(254).pipe(z.email('Enter a valid email address'));
export const name = z.string().trim().min(1, 'Required').max(120);
export const text = (max = 200) => z.string().trim().max(max);
export const requiredText = (max = 200) => z.string().trim().min(1, 'Required').max(max);
export const phone = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^\+?[0-9]{10,15}$/, 'Enter a valid phone number'));
export const gstin = z.string().trim().toUpperCase().regex(GSTIN_RE, 'Invalid GSTIN (15 characters)');
export const pan = z.string().trim().toUpperCase().regex(PAN_RE, 'Invalid PAN');
export const fssai = z.string().trim().regex(FSSAI_RE, 'FSSAI licence number has 14 digits');
export const pincode = z.string().trim().regex(PINCODE_RE, 'Invalid PIN code');
export const isoDate = z.string().regex(ISO_DATE_RE, 'Use YYYY-MM-DD');
export const timezone = z.string().refine(isValidTimezone, 'Unknown timezone');
export const stateCode = z.string().regex(/^\d{2}$/, 'Select a state');
export const reason = z.string().trim().min(3, 'Please give a reason').max(500);
export const cidr = z.string().trim().regex(CIDR_RE, 'Enter an IP or CIDR, e.g. 203.0.113.0/24');

export const outletCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9]{1,3}$/, '2–4 letters/digits, starting with a letter')
  .refine((v) => !/^D\d{3}$/.test(v), 'Codes like D001 are reserved for device series');

export const slug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, digits and dashes only')
  .min(3)
  .max(40);

const COMMON_PASSWORDS = new Set([
  'password12',
  'password123',
  'password@123',
  '1234567890',
  '12345678910',
  'qwertyuiop',
  'iloveyou12',
  'admin12345',
  'welcome123',
  'shripos123',
  'abcdefghij',
  'letmein123',
]);

export const password = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(128, 'Use at most 128 characters')
  .refine((v) => !COMMON_PASSWORDS.has(v.toLowerCase()), 'This password is too common')
  .refine((v) => !/^(.)\1+$/.test(v), 'This password is too simple');

export const pin = z.string().regex(/^\d{4,6}$/, 'PIN must be 4–6 digits');

export const address = z.strictObject({
  line1: requiredText(200),
  line2: optional(text(200)),
  city: requiredText(80),
  state: optional(text(80)),
  stateCode,
  pincode: optional(pincode),
  country: z.string().trim().length(2).default('IN'),
});

export const pagination = {
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
};

export const idParam = z.strictObject({ id: objectId });
